import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DisplayMode, SelectBox } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CodeConversionMstDto, Sgke121aResponseInitialhyojiInfo } from 'sg-api';
import { CodeConversionMstService } from '../../../../../../sg-base/sg-base-shared/services/codeConversionMst.service';
import { UsedSgke122aService } from '../../services/used-sgke122a.service';

@Component({
  selector: 'app-sgke120d-search-condition',
  templateUrl: './sgke120d-search-condition.component.html',
  styleUrl: './sgke120d-search-condition.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class Sgke120dSearchConditionComponent implements OnInit, OnDestroy {
  /** モード */
  @Input() mode: string;
  /** 初期表示データ */
  @Input() initialhyojiInfo: Sgke121aResponseInitialhyojiInfo;
  /** 業務日付 */
  @Input() businessDate: Date = null;

  /** クリアボタン押下Emitter */
  @Output() onClickClearEmitter = new EventEmitter();

  private onDestroy$ = new Subject();
  /** 親フォーム情報 */
  private parentForm: UntypedFormGroup;
  /** モード_登録 */
  public MODE_CREATE = DisplayMode.create;
  /** 入金突合ステータス_未報告 */
  public RECONCILIATION_STATUSCODE_UNREPORTED = "0";
  /** 入金突合ステータス_報告済 */
  public RECONCILIATION_STATUSCODE_REPORTED = "1";

  /** 会社リスト */
  companyList: SelectBox[] = [];
  /** 入金地区リスト */
  rcvmnyAreaList: SelectBox[] = [];
  /** 入金口座リスト */
  rcvmnyKouzaList: SelectBox[] = [];
  /** 入金形態リスト */
  rcvmnyKeitaiList: SelectBox[] = [];
  /** 口座入金機関リスト */
  kouzaRcvmnyFcltList: SelectBox[] = [];

  /** コード変換マスタマップ */
  codeMap: Map<string, CodeConversionMstDto[]>;
  /** 検索条件情報FormGroup */
  searchConditionForm: UntypedFormGroup;

  /**
   * コンストラクタ
   * @param {FormGroupDirective} parent
   * @param {UntypedFormBuilder} fb
   * @param {CodeConversionMstService} codeConvert
   * @param {UsedSgke122aService} searchService
   */
  constructor(
    private parent: FormGroupDirective,
    private fb: UntypedFormBuilder,
    private codeConvert: CodeConversionMstService,
    public searchService: UsedSgke122aService,
  ) {
  }

  /**
   * 初期処理
   * @memberof Sgke120dSearchConditionComponent
   */
  ngOnInit(): void {
    // 親フォーム取得
    this.parentForm = this.parent.form;
    // 前回検索結果を取得
    let form = this.searchService.searchFormValue;
    // セレクトボックス生成
    this.setSelectList();
    // フォーム生成
    this.createForm();

    //コード変換マスタ共通コンポーネント呼び出し
    this.codeConvert.getCodeConversionMst(['S300190']).pipe(takeUntil(this.onDestroy$)).subscribe(codeMap => {
      // ローカルに保存
      this.codeMap = codeMap;
    });

    if (form) {
      const cndt = form.cndt;
      // 前回検索結果を設定
      this.searchConditionForm.reset(cndt);
      // 入金形態変更時処理
      this.onChangeRcvmnyKeitai(cndt.rcvmnyKeitaiCode, cndt.kouzaRcvmnyBankofficecode);
      // 入金突合ステータス変更時処理
      this.onChangeStatus(cndt.rcvmnyReconciliationStatuscode, cndt.reportYearmonthdateStart, cndt.reportYearmonthdateEnd, cndt.reportedKafusoku);
      // 再検索処理
      this.parent.ngSubmit.emit();
    } else {
      // 口座入金機関を非活性に設定
      this.searchConditionForm.controls['kouzaRcvmnyBankofficecode'].disable();
      // 入金突合ステータス変更時処理
      this.onChangeStatus(this.searchConditionForm.value['rcvmnyReconciliationStatuscode']);
    }

  }

  /**
   * 購読解除
   * @memberof Sgke120dSearchConditionComponent
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.parentForm.clearValidators();
    this.parentForm.updateValueAndValidity();
    this.parentForm.removeControl('cndt');
  }

  /**
   * 入金突合ステータス変更時処理
   * @param {string} status 
   * @param {string} reportYearmonthdateStart
   * @param {string} reportYearmonthdateEnd
   * @param {boolean} reportedKafusoku
   * @memberof Sgke120dSearchConditionComponent
   */
  onChangeStatus(status: string, reportYearmonthdateStart: string = null, reportYearmonthdateEnd: string = null, reportedKafusoku: boolean = false): void {
    // 報告年月日（開始）
    let reportDateStartCtrl = this.searchConditionForm.controls['reportYearmonthdateStart'] as UntypedFormControl;
    // 報告年月日（終了）
    let reportDateEndCtrl = this.searchConditionForm.controls['reportYearmonthdateEnd'] as UntypedFormControl;
    // 報告済過不足
    let reportedKafusokuCtrl = this.searchConditionForm.controls['reportedKafusoku'] as UntypedFormControl;

    if (status != this.RECONCILIATION_STATUSCODE_UNREPORTED) {
      reportDateStartCtrl.enable();
      reportDateEndCtrl.enable();
      reportDateEndCtrl.setValue(this.businessDate);
      reportedKafusokuCtrl.enable();
    } else {
      reportDateStartCtrl.disable();
      reportDateStartCtrl.reset(reportYearmonthdateStart);
      reportDateEndCtrl.disable();
      reportDateEndCtrl.reset(reportYearmonthdateEnd);
      reportedKafusokuCtrl.disable();
      reportedKafusokuCtrl.reset(reportedKafusoku);
    }

  }

  /**
   * クリアボタン押下時処理
   * @memberof Sgke120dSearchConditionComponent
   */
  onClickClear(): void {
    this.onClickClearEmitter.emit();
    let kouzaRcvmnyFcltCtrl = this.searchConditionForm.controls['kouzaRcvmnyBankofficecode'];
    // 口座入金機関を非活性に設定
    kouzaRcvmnyFcltCtrl.disable();
    // 入金突合ステータス変更時処理
    this.onChangeStatus(this.searchConditionForm.value['rcvmnyReconciliationStatuscode']);
  }

  /**
   * 入金形態変更時処理
   * @param {string} rcvmnyKeitai
   * @param {string} kouzaRcvmnyFclt 
   * @memberof Sgke120dSearchConditionComponent
   */
  onChangeRcvmnyKeitai(rcvmnyKeitai: string, kouzaRcvmnyFclt: string = null): void {
    // 入金形態リスト
    const parentList = this.initialhyojiInfo?.rcvmnyKeitaiCode;
    // 入金形態に紐づく口座入金機関取得
    const parent = parentList?.find(obj => obj.codeValue.includes(rcvmnyKeitai));
    const childList = parent?.kouzaRcvmnyBankofficecode;
    // 口座入金機関を口座入金機関コンボボックスに設定
    this.kouzaRcvmnyFcltList = childList?.map((child) => {
      let selectBox: SelectBox = {
        value: child.codeValue,
        viewValue: child.aname
      };
      return selectBox;
    });

    let ctrl = this.searchConditionForm.controls['kouzaRcvmnyBankofficecode'];
    // 入金形態が選択状態なら有効、未選択(=null)の場合は無効
    if (rcvmnyKeitai) {
      ctrl.enable();
    } else {
      ctrl.disable();
    }
    ctrl.reset(kouzaRcvmnyFclt);
  }

  /**
   * フォーム作成
   * @private
   * @memberof Sgke120dSearchConditionComponent
   */
  private createForm(): void {
    this.searchConditionForm = this.fb.group({
      // 会社
      companycode: this.fb.nonNullable.control({ value: this.companyList[0].value, disabled: false }),
      // 入金地区
      rcvmnyAreacode: this.fb.nonNullable.control({ value: this.rcvmnyAreaList[0].value, disabled: false }),
      // 口座入金予定年月日（開始）
      kouzaRcvmnyytiymdStart: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 口座入金年月日（終了）
      kouzaRcvmnyytiymdEnd: this.fb.nonNullable.control({ value: this.businessDate, disabled: false }, Validators.required),
      // 入金口座
      rcvmnyKouzacode: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 入金形態
      rcvmnyKeitaiCode: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 口座入金機関
      kouzaRcvmnyBankofficecode: this.fb.nonNullable.control({ value: null, disabled: true }),
      // 入金突合ステータス
      rcvmnyReconciliationStatuscode: this.fb.nonNullable.control({
        value: this.mode == this.MODE_CREATE ? this.RECONCILIATION_STATUSCODE_UNREPORTED : this.RECONCILIATION_STATUSCODE_REPORTED,
        disabled: false
      }, Validators.required),
      // 報告年月日（開始）
      reportYearmonthdateStart: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 報告年月日（終了）
      reportYearmonthdateEnd: this.fb.nonNullable.control({ value: null, disabled: false }),
      // 報告済過不足
      reportedKafusoku: this.fb.nonNullable.control({ value: false, disabled: false }),

    });
    // 親コンポーネントのformに検索条件formを追加
    this.parentForm.addControl("cndt", this.searchConditionForm);
  }

  /**
   * 選択リストに値を設定
   * @private
   * @memberof Sgke120dSearchConditionComponent
   */
  private setSelectList(): void {
    // 会社リスト
    const companycodeList = this.initialhyojiInfo?.companycode;
    this.companyList = companycodeList?.map((companycode) => {
      let selectBox: SelectBox = {
        value: companycode.codeValue,
        viewValue: companycode.aname
      };
      return selectBox;
    });

    // 入金地区リスト
    // 会社に紐づく入金地区取得
    const companycode = companycodeList?.find(obj => obj.codeValue.includes(this.companyList[0].value));
    const rcvmnyAreacodeList = companycode?.rcvmnyAreacode;
    // 入金地区を入金地区コンボボックスに設定
    this.rcvmnyAreaList = rcvmnyAreacodeList?.map((child) => {
      let selectBox: SelectBox = {
        value: child.codeValue,
        viewValue: child.aname
      };
      return selectBox;
    });

    // 入金口座リスト
    this.rcvmnyKouzaList = this.initialhyojiInfo?.rcvmnyKouzacode?.map((rcvmnyKouzacode) => {
      let selectBox: SelectBox = {
        value: rcvmnyKouzacode.codeValue,
        viewValue: rcvmnyKouzacode.aname
      };
      return selectBox;
    });

    // 入金形態リスト
    this.rcvmnyKeitaiList = this.initialhyojiInfo?.rcvmnyKeitaiCode?.map((rcvmnyKeitai) => {
      let selectBox: SelectBox = {
        value: rcvmnyKeitai.codeValue,
        viewValue: rcvmnyKeitai.aname
      };
      return selectBox;
    });
  }

}
